/* Reya · Up next hero — the framed preview expands into the hero (clip-path from the preview's box to full),
   the gold thread redraws along the bottom, and the headline/button stay still. */
(() => {
  if (customElements.get('rr-thread-hero')) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));
  const pad = (n) => String(n).padStart(2, '0');

  customElements.define(
    'rr-thread-hero',
    class extends HTMLElement {
      connectedCallback() {
        this.stage = this.querySelector('.rr-thr__stage');
        this.slides = [...this.querySelectorAll('[data-rr-thr-slide]')];
        this.n = this.slides.length;
        this.i = 0;
        this.heading = this.querySelector('[data-rr-thr-heading]');
        this.slogan = this.querySelector('[data-rr-thr-slogan]');
        this.textEl = this.querySelector('[data-rr-thr-text]');
        this.btn = this.querySelector('[data-rr-thr-btn]');
        this.btnLabel = this.querySelector('[data-rr-thr-btn-label]');
        this.preview = this.querySelector('[data-rr-thr-preview]');
        this.previewImg = this.querySelector('[data-rr-thr-preview-img]');
        this.num = this.querySelector('[data-rr-thr-num]');
        if (this.heading) {
          this.heading.dataset.current = this.slides[0] ? this.slides[0].dataset.heading || '' : '';
          this.heading.dataset.initial = this.heading.dataset.current ? this.heading.dataset.default || '' : this.heading.innerHTML;
        }
        if (this.n < 2) return;
      // later slides load after the page has finished loading (keeps the first view fast)
      const wake = () => this.querySelectorAll('[data-rr-src], [data-rr-srcset]').forEach((el) => {
        if (el.dataset.rrSrcset) { el.srcset = el.dataset.rrSrcset; el.removeAttribute('data-rr-srcset'); }
        if (el.dataset.rrSrc) { el.src = el.dataset.rrSrc; el.removeAttribute('data-rr-src'); }
      });
      this.wake = wake;
      if (document.readyState === 'complete') setTimeout(wake, 1200); else window.addEventListener('load', () => setTimeout(wake, 1200), { once: true });

        this.dur = parseFloat(getComputedStyle(this).getPropertyValue('--rr-thr-dur')) || 950;
        this.speed = (parseFloat(this.dataset.speed) || 7) * 1000;
        this.auto = this.dataset.autoplay === 'true' && !reduce;
        this.elapsed = 0;
        this.hold = false;
        this.visible = true;
        this.setPreview(1);

        this.preview.addEventListener('click', () => this.go(this.i + 1));
        this.querySelector('[data-rr-thr-next]').addEventListener('click', () => this.go(this.i + 1));
        this.querySelector('[data-rr-thr-prev]').addEventListener('click', () => this.go(this.i - 1));
        this.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') this.go(this.i + 1);
          if (e.key === 'ArrowLeft') this.go(this.i - 1);
        });
        this.addEventListener('mouseenter', () => (this.hold = true));
        this.addEventListener('mouseleave', () => (this.hold = false));
        this.addEventListener('focusin', () => (this.hold = true));
        this.addEventListener('focusout', (e) => !this.contains(e.relatedTarget) && (this.hold = false));
        let x0 = null;
        this.addEventListener('touchstart', (e) => (x0 = e.touches[0].clientX), { passive: true });
        this.addEventListener('touchend', (e) => {
          if (x0 == null) return;
          const dx = e.changedTouches[0].clientX - x0;
          x0 = null;
          if (Math.abs(dx) > 40) this.go(this.i + (dx < 0 ? 1 : -1));
        }, { passive: true });
        if ('IntersectionObserver' in window) {
          new IntersectionObserver(([en]) => (this.visible = en.isIntersecting), { threshold: 0.2 }).observe(this);
        }
        if (window.Shopify && Shopify.designMode) {
          this.auto = false;
          document.addEventListener('shopify:block:select', (e) => {
            const k = this.slides.indexOf(e.target.closest('[data-rr-thr-slide]'));
            if (k > -1 && k !== this.i) this.go(k);
          });
        }
        if (this.auto) this.tick();
      }

      thumb(k) {
        const t = this.slides[k].querySelector('template[data-rr-thr-thumb]');
        return t ? t.innerHTML.trim().replace(/&amp;/g, '&') : '';
      }

      setPreview(k) {
        if (!this.previewImg) return;
        const src = this.thumb(((k % this.n) + this.n) % this.n);
        if (src) this.previewImg.src = src;
      }

      drawThread() {
        if (!this.querySelector('[data-rr-thr-draw]') || reduce) return;
        this.classList.add('is-thread-reset');
        void this.offsetWidth;
        this.classList.remove('is-thread-reset');
      }

      tick() {
        let last = Date.now();
        this.timer = setInterval(() => {
          const now = Date.now();
          if (!this.busy && !this.hold && this.visible && !document.hidden) this.elapsed += now - last;
          last = now;
          if (this.elapsed >= this.speed) this.go(this.i + 1);
        }, 250);
      }

      disconnectedCallback() {
        clearInterval(this.timer);
      }

      async go(to) {
        if (this.busy) return;
        to = ((to % this.n) + this.n) % this.n;
        if (to === this.i) return;
        this.busy = true;
        this.elapsed = 0;
        if (this.wake) this.wake();
        const cur = this.slides[this.i];
        const next = this.slides[to];

        // the preview shows the scene that is about to open
        this.setPreview(to);

        // start the incoming slide clipped to the preview frame, then open it to the full hero
        const s = this.stage.getBoundingClientRect();
        const r = this.preview.getBoundingClientRect();
        const box = r.width && getComputedStyle(this.preview).display !== 'none'
          ? `inset(${r.top - s.top}px ${s.right - r.right}px ${s.bottom - r.bottom}px ${r.left - s.left}px)`
          : 'inset(0 0 0 100%)';
        next.style.setProperty('--rr-thr-from', box);
        next.classList.add('is-expanding');
        void next.offsetWidth;
        this.drawThread();

        const h = next.dataset.heading || '';
        const swapHeading = this.heading && (h || this.heading.dataset.current) && h !== (this.heading.dataset.current || '');
        const sl = next.dataset.slogan || '';
        const swapSlogan = this.slogan && sl && sl !== this.slogan.textContent;
        const tx = next.dataset.text || '';
        const swapText = this.textEl && tx && tx !== this.textEl.textContent;

        if (!reduce) {
          this.classList.add('is-go');
          this.preview.classList.add('is-fading');
          if (swapHeading) this.heading.classList.add('is-swap');
          if (swapSlogan) this.slogan.classList.add('is-swap');
          if (swapText) this.textEl.classList.add('is-swap');
          await wait(this.dur + 40);
        }

        cur.classList.remove('is-current');
        next.classList.add('is-current');
        next.classList.remove('is-expanding');
        next.style.removeProperty('--rr-thr-from');
        this.classList.remove('is-go');
        this.i = to;
        if (this.num) this.num.textContent = pad(to + 1);

        if (swapHeading) {
          this.heading.innerHTML = h ? h.replace(/</g, '&lt;') : this.heading.dataset.initial || this.heading.innerHTML;
          this.heading.dataset.current = h;
          this.heading.classList.remove('is-swap');
        }
        if (swapSlogan) {
          this.slogan.textContent = sl;
          this.slogan.classList.remove('is-swap');
        }
        if (swapText) {
          this.textEl.textContent = tx;
          this.textEl.classList.remove('is-swap');
        }
        if (this.btn && next.dataset.btnLink) this.btn.setAttribute('href', next.dataset.btnLink);
        if (this.btnLabel && next.dataset.btnLabel) this.btnLabel.textContent = next.dataset.btnLabel;

        this.setPreview(to + 1);
        await wait(60);
        this.preview.classList.remove('is-fading');
        this.busy = false;
      }
    }
  );
})();
